import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Carousel — a toy merry-go-round: a teal drum platform with a golden edge, a
// cream centre column, a striped red-and-cream cone canopy with a scalloped
// rim, and four plump little toy horses on golden poles. Every pole and horse
// is named "rotor" so the whole ride turns as one about the centre axis —
// slowly on idle, briskly (with a happy bob) on "ride".
//
// Frame: y = 0 is the ground, +Z is the front. 1 unit = 1 m; the carousel is
// a squat ~4 m ride on a 3x3 tile plot.

const RED = "#d8412f";        // canopy canvas
const RED_DARK = "#a92e22";   // shaded canvas / saddle
const CREAM = "#f6efe3";      // stripes, column, horse coats
const CREAM_DARK = "#e4d9c4"; // shaded cream
const TEAL = "#3f9e9b";       // platform drum / saddles
const TEAL_DARK = "#2f7c79";  // platform base step
const GOLD = "#e8b53a";       // poles, trims, finial
const CORAL = "#e2795c";      // the second horse coat
const MANE = "#a92e22";       // horse manes and tails

// ── frame heights ────────────────────────────────────────────────────────────
const DECK_TOP = 0.34;               // riders stand here
const CANOPY_BASE = 2.9;             // cone base height
const CANOPY_R = 2.55;               // cone base radius
const CANOPY_H = 1.05;               // cone rise (apex at 3.95)
const HORSE_R = 1.65;                // horses circle at this radius

// One plump toy horse, authored at its own origin facing +Z (saddle centre at
// the origin), then swung to its place on the ride. All parts named "rotor".
function horseLocal(coat: string, saddle: string): Part[] {
  return [
    // Plump body.
    { geometry: new THREE.BoxGeometry(0.3, 0.32, 0.6), color: coat, position: [0, 0, 0] },
    // Neck leaning forward, head and muzzle.
    { geometry: new THREE.BoxGeometry(0.16, 0.34, 0.18), color: coat,
      position: [0, 0.24, 0.24], rotation: [0.5, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.16, 0.16, 0.28), color: coat, position: [0, 0.42, 0.42] },
    { geometry: new THREE.BoxGeometry(0.12, 0.11, 0.12), color: CREAM_DARK, position: [0, 0.38, 0.56] },
    // Mane running down the back of the neck, and a swishy tail.
    { geometry: new THREE.BoxGeometry(0.08, 0.34, 0.08), color: MANE,
      position: [0, 0.28, 0.13], rotation: [0.5, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.09, 0.3, 0.1), color: MANE,
      position: [0, -0.04, -0.35], rotation: [-0.55, 0, 0] },
    // Saddle.
    { geometry: new THREE.BoxGeometry(0.34, 0.09, 0.26), color: saddle, position: [0, 0.19, -0.02] },
    // Four stubby legs, frozen mid-gallop (front pair kicked forward).
    { geometry: new THREE.BoxGeometry(0.09, 0.3, 0.09), color: coat,
      position: [-0.09, -0.28, 0.21], rotation: [-0.3, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.09, 0.3, 0.09), color: coat,
      position: [0.09, -0.28, 0.21], rotation: [-0.3, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.09, 0.3, 0.09), color: coat,
      position: [-0.09, -0.28, -0.21], rotation: [0.3, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.09, 0.3, 0.09), color: coat,
      position: [0.09, -0.28, -0.21], rotation: [0.3, 0, 0] },
  ];
}

// Swing a local horse (plus its golden pole) to azimuth `az` on the ride,
// facing its direction of travel, saddle centre at height `y`.
function horseAt(az: number, y: number, coat: string, saddle: string): Part[] {
  const yaw = az + Math.PI / 2;   // face along the direction of travel
  const cx = Math.sin(az) * HORSE_R;
  const cz = Math.cos(az) * HORSE_R;
  const cosB = Math.cos(yaw), sinB = Math.sin(yaw);
  const horse = horseLocal(coat, saddle).map((p): Part => {
    const [lx, ly, lz] = p.position ?? [0, 0, 0];
    const [rx, , rz] = p.rotation ?? [0, 0, 0];
    return {
      ...p,
      position: [cx + lx * cosB + lz * sinB, y + ly, cz - lx * sinB + lz * cosB],
      rotation: [rx, yaw, rz],
      eulerOrder: "YXZ",
      name: "rotor",
    };
  });
  return [
    // The golden pole, deck to canopy, skewering the saddle.
    { geometry: new THREE.CylinderGeometry(0.045, 0.045, CANOPY_BASE - DECK_TOP, 8),
      color: GOLD, position: [cx, (CANOPY_BASE + DECK_TOP) / 2, cz], name: "rotor" },
    ...horse,
  ];
}

// Cream stripes lying along the canopy slope.
function canopyStripes(): Part[] {
  const out: Part[] = [];
  const tilt = Math.atan2(CANOPY_R, CANOPY_H);
  const slant = Math.hypot(CANOPY_R, CANOPY_H);
  for (let i = 0; i < 8; i++) {
    const az = (i / 8) * Math.PI * 2 + Math.PI / 8;
    const radial = CANOPY_R * 0.5 + 0.03;
    out.push({
      geometry: new THREE.BoxGeometry(0.42, slant * 0.78, 0.07),
      color: CREAM,
      position: [Math.sin(az) * radial, CANOPY_BASE + CANOPY_H * 0.5 + 0.04, Math.cos(az) * radial],
      rotation: [-tilt, az, 0],
      eulerOrder: "YXZ",
    });
  }
  return out;
}

// The scalloped rim hanging under the canopy edge, alternating red and cream.
function canopyScallops(): Part[] {
  const out: Part[] = [];
  const n = 14;
  for (let i = 0; i < n; i++) {
    const az = (i / n) * Math.PI * 2;
    out.push({
      geometry: new THREE.SphereGeometry(0.26, 10, 8),
      color: i % 2 === 0 ? CREAM : RED,
      position: [Math.sin(az) * (CANOPY_R - 0.05), CANOPY_BASE - 0.08, Math.cos(az) * (CANOPY_R - 0.05)],
      rotation: [0, az, 0],
      scale: [1, 0.85, 0.38],
    });
  }
  return out;
}

export const carousel = defineModel({
  id: "carousel",
  name: "Carousel",
  batchId: "mess",
  tags: ["mess", "cute", "animated", "ride", "fairground", "carousel", "merry-go-round", "horses"],
  summary: "A toy merry-go-round: striped canopy, golden poles and four plump horses that circle the centre column.",
  description:
    "A fairground carousel built like a toy: a teal drum platform with a gold " +
    "edge, a cream centre column, a striped red-and-cream cone canopy with a " +
    "scalloped rim and a golden finial, and four plump little horses on golden " +
    "poles. The whole rotor of poles and horses turns about the centre axis — " +
    "slowly on idle, briskly with a happy bob on ride.",
  author: "models",
  footprint: 2.7,
  tiles: { w: 3, h: 3 },
  parts(): Part[] {
    return [
      // ── the platform ──────────────────────────────────────────────────────
      // A shallow step, the teal drum deck and a golden edge band.
      { geometry: new THREE.CylinderGeometry(2.5, 2.58, 0.14, 22), color: TEAL_DARK,
        position: [0, 0.07, 0] },
      { geometry: new THREE.CylinderGeometry(2.32, 2.4, 0.2, 22), color: TEAL,
        position: [0, 0.24, 0] },
      { geometry: new THREE.CylinderGeometry(2.36, 2.36, 0.06, 22), color: GOLD,
        position: [0, DECK_TOP - 0.03, 0] },

      // ── the centre column ─────────────────────────────────────────────────
      { geometry: new THREE.CylinderGeometry(0.26, 0.3, CANOPY_BASE - DECK_TOP, 14),
        color: CREAM, position: [0, (CANOPY_BASE + DECK_TOP) / 2, 0] },
      // Gold collars at foot and crown, and a red band around the middle.
      { geometry: new THREE.CylinderGeometry(0.36, 0.4, 0.14, 14), color: GOLD,
        position: [0, DECK_TOP + 0.07, 0] },
      { geometry: new THREE.CylinderGeometry(0.34, 0.3, 0.14, 14), color: GOLD,
        position: [0, CANOPY_BASE - 0.1, 0] },
      { geometry: new THREE.CylinderGeometry(0.3, 0.3, 0.3, 14), color: RED,
        position: [0, (CANOPY_BASE + DECK_TOP) / 2, 0] },

      // ── the canopy ────────────────────────────────────────────────────────
      { geometry: new THREE.ConeGeometry(CANOPY_R, CANOPY_H, 22), color: RED,
        position: [0, CANOPY_BASE + CANOPY_H / 2, 0] },
      ...canopyStripes(),
      ...canopyScallops(),
      // Golden finial ball on the peak.
      { geometry: new THREE.SphereGeometry(0.16, 10, 8), color: GOLD,
        position: [0, CANOPY_BASE + CANOPY_H + 0.06, 0] },

      // ── the rotor: four horses on golden poles (all named "rotor") ────────
      ...horseAt(0, 1.15, CREAM, RED_DARK),
      ...horseAt(Math.PI / 2, 1.35, CORAL, TEAL),
      ...horseAt(Math.PI, 1.15, CREAM, TEAL),
      ...horseAt(Math.PI * 1.5, 1.35, CORAL, RED_DARK),
    ];
  },
  animations: [
    // Idle — the ride drifts round lazily, waiting for riders.
    {
      id: "idle",
      label: "Slow turn",
      duration: 14,
      loop: true,
      tracks: [
        {
          target: "rotor",
          pivot: [0, 0, 0],
          rotation: [
            { t: 0, value: [0, 0, 0] },
            { t: 14, value: [0, Math.PI * 2, 0] },
          ],
        },
      ],
    },
    // Ride — the signature motion: a brisk spin with a gentle gallop bob.
    {
      id: "ride",
      label: "Ride spin",
      duration: 5,
      loop: true,
      tracks: [
        {
          target: "rotor",
          pivot: [0, 0, 0],
          rotation: [
            { t: 0, value: [0, 0, 0] },
            { t: 5, value: [0, Math.PI * 2, 0] },
          ],
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.25, value: [0, 0.07, 0], ease: "sine" },
            { t: 2.5, value: [0, 0, 0], ease: "sine" },
            { t: 3.75, value: [0, 0.07, 0], ease: "sine" },
            { t: 5, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
